import React, { useRef, useState } from 'react';
import { Search, X, RotateCw, Calendar as CalendarIcon } from 'lucide-react';
import Calendar from '../../../core/components/dashboard/Calendar/Calendar';
import { useOutsideClick } from '../../hooks/ui/useOutsideClick';
import styles from './PointV2.module.css';

/* 포인트 v2 화면(현황/팜페이포인트/적립 기준) 공용 조각 — Figma 섹션 386:12956 */

export const fmt = n => Number(n || 0).toLocaleString('ko-KR');

export const ymd = d =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const WEEKDAY = ['일', '월', '화', '수', '목', '금', '토'];
const md = d => `${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
const parseYmd = s => {
  const [y, m, d] = String(s).split('-').map(Number);
  return new Date(y, m - 1, d);
};

/**
 * GET /point/stats items(오래된 순) → 표 행(최신순).
 * 일별 라벨은 시안 포맷 "04.08 (수)", 주·월별은 서버 label + sub "04.06 - 04.12"(진행중이면 오늘까지).
 */
export const toStatRows = (items, unit, today) =>
  [...items].reverse().map(it => {
    const start = parseYmd(it.start);
    const end = parseYmd(it.end);
    const shownEnd = it.in_progress && today < end ? today : end;
    return {
      ...it,
      label: unit === 'day' ? `${md(start)} (${WEEKDAY[start.getDay()]})` : it.label,
      sub: unit === 'day' ? null : `${md(start)} - ${md(shownEnd)}`,
    };
  });

/** "03.11 13:26" — 시안 일시 포맷 */
export const mdhm = v => {
  if (!v) return '-';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '-';
  const p = n => String(n).padStart(2, '0');
  return `${p(d.getMonth() + 1)}.${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

/* 채워진 삼각형(icon_element/arrow up·down fill) */
export const Triangle = ({ dir = 'up', size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 14 14" fill="currentColor" aria-hidden="true">
    {dir === 'up' && <path d="M7 3.5 11.5 10h-9z" />}
    {dir === 'down' && <path d="M7 10.5 2.5 4h9z" />}
    {dir === 'left' && <path d="M3.5 7 10 2.5v9z" />}
    {dir === 'right' && <path d="M10.5 7 4 11.5v-9z" />}
  </svg>
);

/** trend_number — 상승 빨강 ▲ / 하락 파랑 ▼ */
export const Trend = ({ value, suffix = '' }) => {
  const up = value >= 0;
  return (
    <span className={`${styles.trend} ${up ? styles.trendUp : styles.trendDown}`}>
      <Triangle dir={up ? 'up' : 'down'} />
      {fmt(Math.abs(value))}
      {suffix}
    </span>
  );
};

/** kpi-grid 409:15503 — items: [{ label, value, right? }] */
export const KpiGrid = ({ items }) => (
  <div className={styles.kpiGrid}>
    {items.map(item => (
      <div key={item.label} className={styles.kpiCard}>
        <div className={styles.kpiTop}>
          <span className={styles.kpiLabel}>{item.label}</span>
          {item.right}
        </div>
        <div className={styles.kpiValue}>{item.value}</div>
      </div>
    ))}
  </div>
);

export const ResetButton = ({ onClick }) => (
  <button type="button" className={styles.resetBtn} onClick={onClick}>
    초기화
    <RotateCw size={16} strokeWidth={1.75} />
  </button>
);

export const SearchBar = ({ value, onChange, placeholder }) => (
  <div className={styles.searchBar}>
    <div className={styles.searchLeft}>
      <Search size={20} color="#71717a" strokeWidth={1.75} />
      <input
        className={styles.searchInput}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
      />
    </div>
    <button type="button" className={styles.iconBtn} onClick={() => onChange('')} aria-label="검색어 지우기">
      <X size={18} strokeWidth={2} />
    </button>
  </div>
);

export const RadioGroup = ({ options, value, onChange }) => (
  <div className={styles.radioGroup}>
    {options.map(opt => (
      <button
        key={opt.key}
        type="button"
        role="radio"
        aria-checked={value === opt.key}
        className={`${styles.radio} ${value === opt.key ? styles.radioActive : ''}`}
        onClick={() => onChange(opt.key)}
      >
        <span className={styles.radioDot} />
        {opt.label}
      </button>
    ))}
  </div>
);

/** 조회기간: 2026-04-08 ~ 2026-04-14 + 캘린더 버튼 (기존 Calendar variant="event" 재사용) */
export const DateRange = ({ range, onChange, children }) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(range);
  const ref = useRef(null);
  useOutsideClick(ref, () => setOpen(false), open);

  return (
    <div className={styles.dateWrap} ref={ref}>
      <div className={styles.dateText}>
        <span className={styles.dateValue}>{ymd(range.start)}</span>
        <span className={styles.dateTilde}>~</span>
        <span className={styles.dateValue}>{ymd(range.end)}</span>
      </div>
      <button
        type="button"
        className={styles.calendarBtn}
        aria-label="기간 선택"
        onClick={() => {
          setDraft(range);
          setOpen(o => !o);
        }}
      >
        <CalendarIcon size={18} strokeWidth={1.75} />
      </button>
      {children}
      {open && (
        <div className={styles.calendarDropdown}>
          <Calendar
            mode="range"
            variant="event"
            value={draft}
            onChange={setDraft}
            onClear={() => setDraft({ start: null, end: null })}
            onClose={() => setOpen(false)}
            onConfirm={() => {
              if (draft?.start && draft?.end) {
                onChange(draft);
                setOpen(false);
              }
            }}
          />
        </div>
      )}
    </div>
  );
};

export const OptionRow = ({ label, narrow, type, children }) => (
  <div className={`${styles.optionRow} ${narrow ? styles.optionRowNarrow : ''}`}>
    <div className={styles.optionLabel}>{label}</div>
    <div className={`${styles.optionBody} ${type ? styles.optionBodyType : ''}`}>{children}</div>
  </div>
);

/** 페이지 번호 창: 1 2 3 … 24 (현재 페이지 기준 3칸 + 처음/끝) */
export const pageWindow = (current, total) => {
  if (total <= 5) return Array.from({ length: total }, (_, i) => i + 1);
  const start = current <= 3 ? 1 : current >= total - 2 ? total - 2 : current - 1;
  const nums = [...new Set([1, start, start + 1, start + 2, total])]
    .filter(n => n >= 1 && n <= total)
    .sort((a, b) => a - b);
  const out = [];
  nums.forEach((n, i) => {
    if (i > 0 && n - nums[i - 1] > 1) out.push('…');
    out.push(n);
  });
  return out;
};

export const Pager = ({ info, currentPage, totalPages, onChange }) => (
  <div className={styles.pager}>
    <span className={styles.pagerInfo}>{info}</span>
    <div className={styles.pagination}>
      <button
        type="button"
        className={styles.pageNav}
        disabled={currentPage <= 1}
        onClick={() => onChange(currentPage - 1)}
      >
        <Triangle dir="left" size={16} />
        이전
      </button>
      {pageWindow(currentPage, totalPages).map((n, i) =>
        n === '…' ? (
          <span key={`e${i}`} className={`${styles.pageNum} ${styles.pageEllipsis}`}>
            ⋯
          </span>
        ) : (
          <button
            key={n}
            type="button"
            className={`${styles.pageNum} ${n === currentPage ? styles.pageNumActive : ''}`}
            onClick={() => onChange(n)}
          >
            {n}
          </button>
        )
      )}
      <button
        type="button"
        className={styles.pageNav}
        disabled={currentPage >= totalPages}
        onClick={() => onChange(currentPage + 1)}
      >
        다음
        <Triangle dir="right" size={16} />
      </button>
    </div>
  </div>
);

/** "1–8 표시" 범위 문자열 */
export const rangeText = (page, size, total) =>
  total === 0 ? '0' : `${(page - 1) * size + 1}–${Math.min(page * size, total)}`;

/** tag 409:17698 — FIXED 정액(파랑) / RATE 정률(초록) */
export const MethodTag = ({ method }) => (
  <span className={`${styles.methodTag} ${method === 'RATE' ? styles.methodRate : styles.methodFixed}`}>
    {method === 'RATE' ? '정률' : '정액'}
  </span>
);

/** FIXED → 100P, RATE → 3% */
export const ruleValue = (method, value) => (method === 'RATE' ? `${value}%` : `${fmt(value)}P`);
